import { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import Cropper from 'react-easy-crop';
import DecartResultPreview from './DecartResultPreview';
import { decartEdit } from '../Services/ApiServices/chatServices';
import '../Components/Styles/ImageEditor.css';

const ImageEditor = ({ image, onSave, onCancel }) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [format, setFormat] = useState('jpeg');
  const [quality, setQuality] = useState(0.9);
  const [activeTab, setActiveTab] = useState('crop');

  const [decartPrompt, setDecartPrompt] = useState('');
  const [decartMode, setDecartMode] = useState('lucy');
  const [isDecartProcessing, setIsDecartProcessing] = useState(false);
  const [decartResult, setDecartResult] = useState(null);
  const [showDecartPreview, setShowDecartPreview] = useState(false);
  const [decartError, setDecartError] = useState(null);

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  /**
   * Create canvas and apply transformations
   */
  const createImage = (url) =>
    new Promise((resolve, reject) => {
      if (!url) {
        reject(new Error('No image URL provided'));
        return;
      }
      const img = new Image();
      img.addEventListener('load', () => resolve(img));
      img.addEventListener('error', () => {
        reject(new Error('Failed to load image. The source may be invalid or corrupted.'));
      });
      img.setAttribute('crossOrigin', 'anonymous');
      img.src = url;
    });

  const getRadianAngle = (degreeValue) => {
    return (degreeValue * Math.PI) / 180;
  };

  /**
   * Rotate and crop image
   */
  const getCroppedImg = async (imageSrc, pixelCrop, rotation = 0, format = 'jpeg', quality = 0.9) => {
    const image = await createImage(imageSrc);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    const maxSize = Math.max(image.width, image.height);
    const safeArea = 2 * ((maxSize / 2) * Math.sqrt(2));

    canvas.width = safeArea;
    canvas.height = safeArea;

    ctx.translate(safeArea / 2, safeArea / 2);
    ctx.rotate(getRadianAngle(rotation));
    ctx.translate(-safeArea / 2, -safeArea / 2);

    ctx.drawImage(
      image,
      safeArea / 2 - image.width * 0.5,
      safeArea / 2 - image.height * 0.5
    );

    const data = ctx.getImageData(0, 0, safeArea, safeArea);

    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;

    ctx.putImageData(
      data,
      Math.round(0 - safeArea / 2 + image.width * 0.5 - pixelCrop.x),
      Math.round(0 - safeArea / 2 + image.height * 0.5 - pixelCrop.y)
    );

    // Convert to desired format
    const mimeType = format === 'png' ? 'image/png' : format === 'webp' ? 'image/webp' : 'image/jpeg';
    return canvas.toDataURL(mimeType, quality);
  };

  const handleSave = async () => {
    try {
      const croppedImage = await getCroppedImg(
        image,
        croppedAreaPixels,
        rotation,
        format,
        quality
      );
      
      // Get file size in KB
      const base64Length = croppedImage.length - (croppedImage.indexOf(',') + 1);
      const sizeInBytes = (base64Length * 3) / 4;
      const sizeInKB = Math.round(sizeInBytes / 1024);

      onSave({
        src: croppedImage,
        format,
        size: sizeInKB,
        edited: true,
        rotation,
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      console.error('Error saving image:', error);
      alert('Failed to save image. Please try again.');
    }
  };

  const handleRotate = (degrees) => {
    setRotation((prev) => (prev + degrees) % 360);
  };

  const handleReset = () => {
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setRotation(0);
    setFormat('jpeg');
    setQuality(0.9);
  };

  const dataURLtoBlob = (dataURL) => {
    if (!dataURL || typeof dataURL !== 'string') return null;

    // Expect: data:<mime>;base64,<payload>
    const parts = dataURL.split(';base64,');
    if (!parts || parts.length < 2) return null;

    let contentType = parts[0]?.split(':')[1] || 'image/jpeg';
    // Normalize JPEG type
    if (contentType === 'image/jpg') contentType = 'image/jpeg';

    try {
      const byteCharacters = atob(parts[1]);
      const byteArrays = [];
      for (let offset = 0; offset < byteCharacters.length; offset += 512) {
        const slice = byteCharacters.slice(offset, offset + 512);
        const byteNumbers = new Array(slice.length);
        for (let i = 0; i < slice.length; i++) {
          byteNumbers[i] = slice.charCodeAt(i);
        }
        byteArrays.push(new Uint8Array(byteNumbers));
      }
      return new Blob(byteArrays, { type: contentType });
    } catch (e) {
      // This can happen if the string isn't valid base64.
      // Return null so upstream can block invalid uploads.
      return null;
    }
  };

  const handleDecartProcess = async () => {
    if (!image) {
      setDecartError('No image available for processing');
      return;
    }
    setIsDecartProcessing(true);
    setDecartError(null);
    
    try {
      const blob = dataURLtoBlob(image);
      if (!blob || blob.size === 0) {
        // Prevent INVALID_INPUT from Decart by blocking non-base64/incorrect data URLs.
        throw new Error('Invalid image data for Decart (expected base64 data URL)');
      }

      console.log('[DEBUG] Blob size:', blob.size, 'type:', blob.type);
      
      // Use the original blob mime where possible; Decart is strict about input type.
      const initialType = blob.type && blob.type.startsWith('image/') ? blob.type : 'image/jpeg';
      const file = new File([blob], 'editor-image', { type: initialType });
      console.log('[DEBUG] File:', file.name, file.size, file.type);
      
      const promptValue = decartPrompt && decartPrompt.trim().length > 0
        ? decartPrompt
        : 'Apply professional photo enhancement, improve lighting, color balance, and sharpness';
      const modeValue = ['lucy', 'virtual-try-on'].includes(decartMode) ? decartMode : 'lucy';
      console.log('[DEBUG] Prompt:', promptValue, 'Mode:', modeValue);
      
      // Ensure the uploaded blob is a real image type; otherwise Decart returns INVALID_INPUT.
      const safeContentType = blob.type && blob.type.startsWith('image/') ? blob.type : 'image/jpeg';
      const safeFile = new File([blob], 'editor-image', { type: safeContentType });

      const formData = new FormData();
      formData.append('image', safeFile);
      formData.append('prompt', promptValue);
      formData.append('mode', modeValue);

      
      await decartEdit(
        formData,
        (res) => {
          const payload = res?.data;

          const backendType = payload?.type;
          const backendData = payload?.data;

          const isBackendBase64String =
            typeof backendData === 'string' && backendData.length > 0;

          if (isBackendBase64String) {
            setDecartResult(backendData);
            setShowDecartPreview(true);
            return;
          }

          // Some legacy backends might return base64 directly as res.data
          if (typeof payload === 'string' && payload.length > 0) {
            setDecartResult(payload);
            setShowDecartPreview(true);
            return;
          }

          throw new Error('Invalid response from Decart API - no base64 returned');
        },
        (err) => {
          const errorMsg =
            err?.response?.data?.message ||
            err?.response?.data?.error ||
            err?.message ||
            'Failed to process image with Decart AI';
          console.error('[DEBUG] API error details:', err?.response);
          setDecartError(errorMsg);
          throw new Error(errorMsg);
        }
      );
    } catch (error) {
      console.error('[DEBUG] Decart processing error:', error.message);
      setDecartError(error.message);
    } finally {
      setIsDecartProcessing(false);
    }
  };

  const handleDecartSave = (resultData) => {
    onSave({
      src: resultData.src,
      type: 'decart',
      format: resultData.format || 'jpeg',
      size: resultData.size,
      edited: true,
      timestamp: new Date().toISOString()
    });
    setShowDecartPreview(false);
    setDecartResult(null);
    setDecartPrompt('');
  };

  const handleDecartCancel = () => {
    setShowDecartPreview(false);
    setDecartResult(null);
    setDecartError(null);
  };

  return (
    <div className="image-editor-overlay">
      <div className="image-editor-modal">
        <div className="image-editor-header">
          <h3>✂️ Edit Image</h3>
          <button 
            className="editor-close-btn" 
            onClick={onCancel}
            aria-label="Close editor"
          >
            ✕
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="editor-tabs">
          <button
            className={`editor-tab ${activeTab === 'crop' ? 'active' : ''}`}
            onClick={() => setActiveTab('crop')}
          >
            ✂️ Crop & Zoom
          </button>
          <button
            className={`editor-tab ${activeTab === 'adjust' ? 'active' : ''}`}
            onClick={() => setActiveTab('adjust')}
          >
            🔄 Rotate
          </button>
          <button
            className={`editor-tab ${activeTab === 'format' ? 'active' : ''}`}
            onClick={() => setActiveTab('format')}
          >
            💾 Format
          </button>
          <button
            className={`editor-tab ${activeTab === 'decart' ? 'active' : ''}`}
            onClick={() => setActiveTab('decart')}
          >
            🤖 Decart AI
          </button>
        </div>

        {showDecartPreview && decartResult && (
          <DecartResultPreview
            result={decartResult}
            onSave={handleDecartSave}
            onCancel={handleDecartCancel}
          />
        )}

        {/* Crop Area */}
        <div className="image-editor-crop-area">
          <Cropper
            image={image}
            crop={crop}
            zoom={zoom}
            rotation={rotation}
            aspect={4 / 3}
            onCropChange={setCrop}
            onCropComplete={onCropComplete}
            onZoomChange={setZoom}
            onRotationChange={setRotation}
          />
        </div>

        {/* Controls Panel */}
        <div className="editor-controls">
          {activeTab === 'crop' && (
            <div className="control-section">
              <div className="control-group">
                <label htmlFor="zoom-slider">
                  🔍 Zoom: {zoom.toFixed(1)}x
                </label>
                <input
                  id="zoom-slider"
                  type="range"
                  min={1}
                  max={3}
                  step={0.1}
                  value={zoom}
                  onChange={(e) => setZoom(parseFloat(e.target.value))}
                  className="slider-imageeditor"
                />
              </div>
              <div className="control-info">
                <p>💡 Use pinch gestures or scroll to zoom</p>
                <p>📱 Drag to reposition the crop area</p>
              </div>
            </div>
          )}

          {activeTab === 'adjust' && (
            <div className="control-section">
              <div className="control-group">
                <label>🔄 Rotation: {rotation}°</label>
                <input
                  type="range"
                  min={0}
                  max={360}
                  value={rotation}
                  onChange={(e) => setRotation(parseInt(e.target.value))}
                  className="slider-imageeditor"
                />
              </div>
              <div className="rotation-buttons">
                <button onClick={() => handleRotate(-90)} className="rotate-btn">
                  ↶ 90° Left
                </button>
                <button onClick={() => handleRotate(90)} className="rotate-btn">
                  ↷ 90° Right
                </button>
                <button onClick={() => handleRotate(180)} className="rotate-btn">
                  ↻ 180°
                </button>
              </div>
            </div>
          )}

          {activeTab === 'format' && (
            <div className="control-section">
              <div className="control-group">
                <label>📁 Format</label>
                <div className="format-options">
                  <button
                    className={`format-btn ${format === 'jpeg' ? 'active' : ''}`}
                    onClick={() => setFormat('jpeg')}
                  >
                    JPEG
                  </button>
                  <button
                    className={`format-btn ${format === 'png' ? 'active' : ''}`}
                    onClick={() => setFormat('png')}
                  >
                    PNG
                  </button>
                  <button
                    className={`format-btn ${format === 'webp' ? 'active' : ''}`}
                    onClick={() => setFormat('webp')}
                  >
                    WebP
                  </button>
                </div>
              </div>

              {(format === 'jpeg' || format === 'webp') && (
                <div className="control-group">
                  <label htmlFor="quality-slider">
                    🎨 Quality: {Math.round(quality * 100)}%
                  </label>
                  <input
                    id="quality-slider"
                    type="range"
                    min={0.1}
                    max={1}
                    step={0.1}
                    value={quality}
                    onChange={(e) => setQuality(parseFloat(e.target.value))}
                    className="slider-imageeditor"
                  />
                </div>
              )}

              <div className="format-info">
                <p><strong>JPEG:</strong> Smaller size, good for photos</p>
                <p><strong>PNG:</strong> Lossless, supports transparency</p>
                <p><strong>WebP:</strong> Modern format, best compression</p>
              </div>
            </div>
          )}

          {activeTab === 'decart' && (
            <div className="control-section">
              <div className="control-group">
                <label>🤖 Decart AI Prompt (optional)</label>
                <input
                  type="text"
                  className="decart-prompt-input"
                  placeholder="Enter AI enhancement prompt..."
                  value={decartPrompt}
                  onChange={(e) => setDecartPrompt(e.target.value)}
                />
              </div>
              <div className="control-group">
                <label>🎯 Processing Mode</label>
                <select
                  className="decart-mode-select"
                  value={decartMode}
                  onChange={(e) => setDecartMode(e.target.value)}
                >
                  <option value="lucy">Lucy (General Edit)</option>
                  <option value="virtual-try-on">Virtual Try-On</option>
                </select>
              </div>
              {decartError && (
                <div className="decart-error-message" style={{ color: '#ff4444', marginTop: '8px', fontSize: '13px' }}>
                  ⚠️ {decartError}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="editor-actions">
          <button className="editor-btn reset-btn" onClick={handleReset}>
            🔄 Reset
          </button>
          <button className="editor-btn img-cancel-btn" onClick={onCancel}>
            ✕ Cancel
          </button>
          <button
            className={`editor-btn decart-process-btn ${activeTab === 'decart' ? 'active' : ''}`}
            onClick={handleDecartProcess}
            disabled={isDecartProcessing}
            style={{ display: activeTab === 'decart' ? 'flex' : 'none' }}
          >
            {isDecartProcessing ? '⏳ Processing...' : '🤖 Decart AI'}
          </button>
          <button
            className="editor-btn save-btn"
            onClick={handleSave}
            style={{ display: activeTab === 'decart' ? 'none' : 'flex' }}
          >
            ✓ Save & Apply
          </button>
        </div>
      </div>
    </div>
  );
};

ImageEditor.propTypes = {
  image: PropTypes.string.isRequired,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

export default ImageEditor;
